import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { Html5Qrcode } from 'html5-qrcode';

const QRCodeScanner: React.FC = () => {
  const { currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scannedData, setScannedData] = useState<any>(null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [linking, setLinking] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'teacher') {
      navigate('/teacher/dashboard');
      return;
    }
  }, [currentUser, navigate]);

  // Initialize scanner when scanning starts
  useEffect(() => {
    if (scanning && scannerElementRef.current) {
      const initializeScanner = async () => {
        try {
          // Clear any existing scanner
          if (scannerRef.current) {
            await scannerRef.current.clear();
          }

          // Create new scanner instance
          scannerRef.current = new Html5Qrcode("qr-reader");

          // Get available cameras
          const cameras = await Html5Qrcode.getCameras();
          if (cameras && cameras.length) {
            // Use the back camera if available, otherwise use the first camera
            const cameraId = cameras.find(camera => 
              camera.label.toLowerCase().includes('back') || 
              camera.label.toLowerCase().includes('rear')
            )?.id || cameras[0].id;

            // Start scanning with camera
            await scannerRef.current.start(
              cameraId,
              {
                fps: 10,
                qrbox: { width: 250, height: 250 },
                aspectRatio: 1.0
              },
              (decodedText, decodedResult) => {
                console.log('QR Code detected:', decodedText);
                handleQRCodeDetected(decodedText);
              },
              (error) => {
                // Ignore common errors that don't need to be shown to user
                if (error && !error.includes('No MultiFormat Readers')) {
                  console.log('QR Code scan error:', error);
                }
              }
            );
          } else {
            throw new Error('No cameras found');
          }
        } catch (err) {
          console.error('Error initializing QR scanner:', err);
          setError('Failed to start camera. Please check permissions.');
          setScanning(false);
        }
      };

      // Small delay to ensure the DOM element is rendered
      setTimeout(initializeScanner, 100);
    }
  }, [scanning]);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
    };
  }, []);

  const startScanning = () => {
    try {
      setError('');
      setSuccess('');
      setScanning(true);
    } catch (err) {
      console.error('Error starting QR scanner:', err);
      setError('Failed to start camera. Please check permissions.');
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    setScanning(false);
    if (scannerRef.current) {
      try {
        await scannerRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
      scannerRef.current = null;
    }
  };

  const handleQRCodeDetected = async (decodedText: string) => {
    try {
      setError('');
      setLoadingStudent(true);

      // Try to parse as JSON first (in case it's a full QR data object)
      let qrData;
      try {
        qrData = JSON.parse(decodedText);
      } catch (err) {
        // If not JSON, treat as student ID directly
        qrData = {
          type: 'student-link',
          studentId: decodedText
        };
      }

      // Validate the QR data
      if (qrData.type === 'student-link' && qrData.studentId) {
        // Try to fetch student name
        try {
          const studentDoc = await getDoc(doc(db, 'users', qrData.studentId));
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            setScannedData({
              type: 'student-link',
              studentId: qrData.studentId,
              studentName: studentData.name || studentData.nickname || 'Student'
            });
          } else {
            setScannedData({
              type: 'student-link',
              studentId: qrData.studentId,
              studentName: 'Student (Profile not found)'
            });
          }
        } catch (fetchErr) {
          console.warn('Could not fetch student details:', fetchErr);
          setScannedData({
            type: 'student-link',
            studentId: qrData.studentId,
            studentName: 'Student (Name will be loaded)'
          });
        }
      } else {
        setError('Invalid QR code format. Please scan a valid student QR code.');
      }
    } catch (err) {
      console.error('Error processing QR code:', err);
      setError('Failed to process QR code. Please try again.');
    } finally {
      setLoadingStudent(false);
      stopScanning();
    }
  };

  const handleManualInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value.trim();
    setError(''); // Clear any previous errors
    
    if (input.length === 0) {
      setScannedData(null);
      return;
    }

    // Check if it's a valid student ID format (Firebase UID format)
    if (input.length < 20) {
      setError('Student ID appears to be too short. Please check the ID and try again.');
      return;
    }

    try {
      // Try to parse as JSON first (in case they pasted full QR data)
      const qrData = JSON.parse(input);
      if (qrData.type === 'student-link' && qrData.studentId) {
        setScannedData(qrData);
      } else {
        setError('Invalid QR code data format');
      }
    } catch (err) {
      // If not JSON, treat as student ID directly
      // Try to fetch student name immediately
      setLoadingStudent(true);
      try {
        const studentDoc = await getDoc(doc(db, 'users', input));
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          setScannedData({
            type: 'student-link',
            studentId: input,
            studentName: studentData.name || studentData.nickname || 'Student'
          });
        } else {
          setScannedData({
            type: 'student-link',
            studentId: input,
            studentName: 'Student (Profile not found)'
          });
        }
      } catch (fetchErr) {
        console.warn('Could not fetch student details:', fetchErr);
        setScannedData({
          type: 'student-link',
          studentId: input,
          studentName: 'Student (Name will be loaded)'
        });
      } finally {
        setLoadingStudent(false);
      }
    }
  };

  const linkStudent = async () => {
    if (!scannedData || !currentUser) return;

    try {
      setError('');
      setSuccess('');
      setLinking(true);

      // Validate that we have valid IDs
      if (!currentUser.uid || !scannedData.studentId) {
        console.error('Missing user IDs:', { 
          teacherId: currentUser.uid, 
          studentId: scannedData.studentId 
        });
        setError('Invalid user data. Please try logging in again.');
        return;
      }

      // Validate that IDs don't contain invalid characters
      if (currentUser.uid.includes('/') || scannedData.studentId.includes('/') || 
          currentUser.uid.includes('//') || scannedData.studentId.includes('//')) {
        console.error('Invalid characters in IDs:', { 
          teacherId: currentUser.uid, 
          studentId: scannedData.studentId 
        });
        setError('Invalid user ID format. Please contact support.');
        return;
      }

      // Additional validation for empty strings or whitespace
      if (currentUser.uid.trim() === '' || scannedData.studentId.trim() === '') {
        console.error('Empty user IDs:', { 
          teacherId: currentUser.uid, 
          studentId: scannedData.studentId 
        });
        setError('Invalid user ID format. Please contact support.');
        return;
      }

      // Linking student with teacher

      // Check if student is already linked
      if (currentUser.linkedStudents?.includes(scannedData.studentId)) {
        setError('This student is already linked to your account');
        return;
      }

      // Use the already loaded student name, or try to fetch it
      let studentName = scannedData.studentName;
      if (studentName === 'Student (Name will be loaded)' || studentName === 'Student (Profile not found)') {
        try {
          const studentDoc = await getDoc(doc(db, 'users', scannedData.studentId));
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            studentName = studentData.name || studentData.nickname || 'Student';
          }
        } catch (err) {
          console.warn('Could not fetch student details:', err);
          // Continue with linking even if we can't fetch the name
        }
      }

          // Validate user IDs before using them in document paths
      
      if (!currentUser.uid || !scannedData.studentId) {
        throw new Error('Missing user IDs');
      }
      
      if (currentUser.uid.includes('/') || scannedData.studentId.includes('/')) {
        throw new Error('User IDs contain invalid characters');
      }

      // Use Cloud Function to link teacher and student
      const linkTeacherStudent = httpsCallable(functions, 'linkTeacherStudent');
      const result = await linkTeacherStudent({
        teacherId: currentUser.uid,
        studentId: scannedData.studentId
      });
      console.log('Link result:', result.data);

      setSuccess(`Successfully linked with ${studentName}!`);
      setScannedData(null);
      
      // Try to refresh user data, but don't block on errors
      if (refreshUserData) {
        try {
          await refreshUserData();
        } catch (refreshError) {
          console.warn('Could not refresh user data, but linking was successful:', refreshError);
          // Continue anyway since the linking was successful
        }
      }
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/teacher/dashboard');
      }, 2000);

    } catch (err: any) {
      console.error('Error linking student:', err);
      // Check if the error is related to refreshUserData or actual linking failure
      if (err instanceof Error && err.message.includes('refreshUserData')) {
        // If it's a refresh error, still show success since linking worked
        setSuccess(`Successfully linked with ${scannedData?.studentName || 'Student'}! (Note: Some data may not refresh immediately)`);
        setScannedData(null);
        setTimeout(() => {
          navigate('/teacher/dashboard');
        }, 2000);
      } else if (err.code === 'functions/failed-precondition') {
        // Handle the specific error for students already linked to another teacher
        setError(err.message || 'This student is already linked to another teacher. Students can only be linked to one teacher at a time.');
      } else {
        setError('Failed to link student. Please check the Student ID and try again.');
      }
    } finally {
      setLinking(false);
    }
  };

  const cancelLinking = () => {
    setScannedData(null);
    setError('');
    setSuccess('');
    setLinking(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                Scan Student QR Code
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/teacher/dashboard')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Link with a Student</h2>
            
            {!scanning && !scannedData && (
              <div className="space-y-8">
                {/* QR Code Scanning Option */}
                <div className="text-center">
                  <div className="mb-6">
                    <svg className="mx-auto h-16 w-16 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Option 1: Scan QR Code</h3>
                  <p className="text-gray-600 mb-4">
                    Have the student show you their QR code from their profile page
                  </p>
                  <button
                    onClick={startScanning}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-md font-medium"
                  >
                    Start Camera Scan
                  </button>
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-50 text-gray-500">OR</span>
                  </div>
                </div>

                {/* Manual Input Option */}
                <div className="text-center">
                  <div className="mb-6">
                    <svg className="mx-auto h-16 w-16 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Option 2: Enter Student ID</h3>
                  <p className="text-gray-600 mb-4">
                    Ask the student for their Student ID from their profile page
                  </p>
                  
                  <div className="max-w-md mx-auto">
                    <label htmlFor="manual-input" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Student ID:
                    </label>
                    <div className="relative">
                      <input
                        id="manual-input"
                        type="text"
                        placeholder="Enter student ID here..."
                        onChange={handleManualInput}
                        className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center font-mono"
                      />
                      <button
                        onClick={() => {
                          const input = document.getElementById('manual-input') as HTMLInputElement;
                          if (input) {
                            input.value = '';
                            setScannedData(null);
                            setError('');
                          }
                        }}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 px-2 py-1 text-sm"
                        title="Clear input"
                      >
                        Clear
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      The student can find their ID on their profile page
                    </p>
                    {loadingStudent && (
                      <div className="mt-2 text-center">
                        <div className="inline-flex items-center text-sm text-indigo-600">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading student details...
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {scanning && (
              <div className="text-center">
                <div className="mb-6">
                  <div 
                    ref={scannerElementRef}
                    id="qr-reader"
                    className="w-full max-w-md mx-auto"
                  ></div>
                </div>
                <p className="text-gray-600 mb-4">Point your camera at the student's QR code</p>
                <button
                  onClick={stopScanning}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-medium"
                >
                  Stop Scanning
                </button>
              </div>
            )}

            {scannedData && (
              <div className="text-center">
                <div className="mb-6">
                  <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Student Found!</h3>
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600">Student Name: <span className="font-medium">{scannedData.studentName || 'Unknown'}</span></p>
                  <p className="text-sm text-gray-600">Student ID: <span className="font-mono text-xs">{scannedData.studentId}</span></p>
                </div>
                
                <div className="flex flex-col items-center space-y-4">
                  {/* Progress Bar */}
                  {linking && (
                    <div className="w-full max-w-xs">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>Linking student...</span>
                        <span>Please wait</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full animate-pulse" style={{width: '100%'}}></div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={linkStudent}
                      disabled={linking}
                      className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                        linking 
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {linking ? (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          <span>Linking...</span>
                        </div>
                      ) : (
                        'Link Student'
                      )}
                    </button>
                    <button
                      onClick={cancelLinking}
                      disabled={linking}
                      className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                        linking 
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                          : 'bg-gray-600 hover:bg-gray-700 text-white'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="mt-6 bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-green-600 text-sm">{success}</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default QRCodeScanner;
